import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listLedger,
  listCoverageGaps,
  customerBillingSummary,
  explainLedgerEntry,
  flagDispute,
  generateBillingV2,
} from "@/lib/billing-platform.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertTriangle, FileText, FileSignature, Flag, Layers, ReceiptText } from "lucide-react";
import { toast } from "sonner";
import { runServerFn } from "@/lib/server-fn";

export const Route = createFileRoute("/_authenticated/app/admin/billing-platform")({
  component: BillingPlatform,
});

const fmtMoney = (cents: number) =>
  (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

function BillingPlatform() {
  const { tenantId, customers } = useScope();
  const qc = useQueryClient();
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [explainId, setExplainId] = useState<string | null>(null);
  const [disputeRow, setDisputeRow] = useState<any | null>(null);
  const [disputeReason, setDisputeReason] = useState("");

  const llFn = useServerFn(listLedger);
  const cgFn = useServerFn(listCoverageGaps);
  const sumFn = useServerFn(customerBillingSummary);
  const expFn = useServerFn(explainLedgerEntry);
  const flagFn = useServerFn(flagDispute);
  const genFn = useServerFn(generateBillingV2);

  const enabled = !!tenantId;

  const { data: gapsData } = useQuery({
    queryKey: ["billing-coverage-gaps", tenantId],
    enabled,
    queryFn: () => cgFn({ data: { tenantId: tenantId! } }),
  });

  const { data: ledgerData, isLoading: lLoading } = useQuery({
    queryKey: ["billing-ledger", tenantId, customerId],
    enabled,
    queryFn: () =>
      llFn({ data: { tenantId: tenantId!, customerId: customerId ?? undefined, limit: 200 } }),
  });

  const { data: summary } = useQuery({
    queryKey: ["billing-customer-summary", tenantId, customerId],
    enabled: enabled && !!customerId,
    queryFn: () => sumFn({ data: { tenantId: tenantId!, customerId: customerId! } }),
  });

  const { data: explanation } = useQuery({
    queryKey: ["billing-explain", explainId],
    enabled: !!explainId,
    queryFn: () => expFn({ data: { ledgerId: explainId! } }),
  });

  const totals = useMemo(() => {
    const ledger = ledgerData?.ledger ?? [];
    let charges = 0;
    let credits = 0;
    for (const r of ledger as any[]) {
      if (r.entry_kind === "credit") credits += Number(r.amount_cents);
      else charges += Number(r.amount_cents);
    }
    return { charges, credits, net: charges - credits, count: ledger.length };
  }, [ledgerData]);

  const onGenerate = async () => {
    if (!tenantId || !customerId) {
      toast.error("Pick a customer first");
      return;
    }
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
      .toISOString()
      .slice(0, 10);
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0))
      .toISOString()
      .slice(0, 10);
    const res = await runServerFn(
      () => genFn({ data: { tenantId, customerId, periodStart: start, periodEnd: end } }),
      "Generate invoice",
    );
    if (res.invoice_id) {
      toast.success(`Invoice ${fmtMoney(res.total_cents)} — ${res.lines_generated} lines`);
      qc.invalidateQueries({ queryKey: ["billing-ledger"] });
      qc.invalidateQueries({ queryKey: ["billing-customer-summary"] });
    } else {
      toast.message("No unbilled activity in current period.");
    }
  };

  const onSubmitDispute = async () => {
    if (!disputeRow || !tenantId) return;
    await runServerFn(
      () =>
        flagFn({
          data: {
            tenantId,
            ledgerId: disputeRow.id,
            customerId: disputeRow.customer_id,
            reason: disputeReason,
          },
        }),
      "Flag dispute",
    );
    toast.success("Dispute flagged");
    setDisputeRow(null);
    setDisputeReason("");
    qc.invalidateQueries({ queryKey: ["billing-customer-summary"] });
  };

  const tenantCustomers = useMemo(
    () => (tenantId ? customers.filter((c) => c.tenant_id === tenantId) : []),
    [customers, tenantId],
  );

  return (
    <>
      <PageHeader
        title="Billing Platform"
        description="Operational truth → billable events → ledger → invoice. Every charge explains itself."
        actions={
          <div className="flex items-center gap-2">
            <Select
              value={customerId ?? "__all"}
              onValueChange={(v) => setCustomerId(v === "__all" ? null : v)}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="All customers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All customers</SelectItem>
                {tenantCustomers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={onGenerate} disabled={!customerId}>
              <ReceiptText className="h-4 w-4 mr-1" /> Generate this month
            </Button>
            <Button size="sm" variant="outline" disabled={!customerId} asChild>
              <Link
                to="/app/admin/billing-platform/contracts/$customerId"
                params={{ customerId: customerId ?? "" }}
              >
                <FileSignature className="h-4 w-4 mr-1" /> Contract
              </Link>
            </Button>
          </div>
        }
      />

      <div className="px-4 sm:px-6 py-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          label="This month"
          value={summary ? fmtMoney(summary.totalCurrentCents) : "—"}
          sub={
            summary
              ? `${summary.deltaCents >= 0 ? "+" : ""}${fmtMoney(summary.deltaCents)} vs prior`
              : "Pick a customer"
          }
          icon={ReceiptText}
        />
        <SummaryCard
          label="Ledger entries"
          value={totals.count.toLocaleString()}
          sub={`${fmtMoney(totals.charges)} charges · ${fmtMoney(totals.credits)} credits`}
          icon={Layers}
        />
        <SummaryCard
          label="Open disputes"
          value={summary ? String(summary.openDisputes) : "—"}
          sub={summary ? `${summary.totalDisputes} total` : "Pick a customer"}
          icon={Flag}
        />
        <SummaryCard
          label="Coverage gaps"
          value={String(
            (gapsData?.gaps ?? []).filter((g: any) => Number(g.gap_pct) >= 50).length,
          )}
          sub="Event types not yet emitting"
          icon={AlertTriangle}
        />
      </div>

      <div className="px-4 sm:px-6 pb-6">
        <Tabs defaultValue="ledger" className="w-full">
          <TabsList>
            <TabsTrigger value="ledger">Ledger</TabsTrigger>
            <TabsTrigger value="bytype">By event type</TabsTrigger>
            <TabsTrigger value="coverage">Coverage gaps</TabsTrigger>
          </TabsList>

          <TabsContent value="ledger" className="mt-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Billing ledger</CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                {lLoading ? (
                  <div className="p-6 text-sm text-muted-foreground text-center">Loading…</div>
                ) : (ledgerData?.ledger ?? []).length === 0 ? (
                  <div className="p-6 text-sm text-muted-foreground text-center">
                    No ledger entries.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-xs text-muted-foreground">
                      <tr>
                        <th className="text-left p-3">When</th>
                        <th className="text-left p-3">Kind</th>
                        <th className="text-left p-3">Event</th>
                        <th className="text-right p-3">Qty</th>
                        <th className="text-right p-3">Rate</th>
                        <th className="text-right p-3">Amount</th>
                        <th className="text-left p-3">Invoice</th>
                        <th className="text-right p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(ledgerData?.ledger ?? []).map((r: any) => (
                        <tr key={r.id} className="border-t">
                          <td className="p-3 tabular-nums text-xs">
                            {new Date(r.occurred_at).toLocaleString()}
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className="text-[10px]">
                              {r.entry_kind}
                            </Badge>
                          </td>
                          <td className="p-3 font-mono text-xs">{r.event_type ?? "—"}</td>
                          <td className="p-3 text-right tabular-nums">{Number(r.quantity)}</td>
                          <td className="p-3 text-right tabular-nums">
                            {fmtMoney(Number(r.unit_rate_cents))}
                          </td>
                          <td className="p-3 text-right tabular-nums font-medium">
                            {fmtMoney(Number(r.amount_cents))}
                          </td>
                          <td className="p-3 text-xs">
                            {r.invoice_id ? (
                              <Badge variant="secondary" className="text-[10px]">
                                invoiced
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="p-3 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setExplainId(r.id)}
                            >
                              <FileText className="h-3.5 w-3.5 mr-1" /> Explain
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDisputeRow(r)}
                            >
                              <Flag className="h-3.5 w-3.5 mr-1" /> Dispute
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bytype" className="mt-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">By event type — this month</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {!summary ? (
                  <div className="p-6 text-sm text-muted-foreground text-center">
                    Pick a customer.
                  </div>
                ) : Object.keys(summary.byEventType).length === 0 ? (
                  <div className="p-6 text-sm text-muted-foreground text-center">
                    No charges this month.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-xs text-muted-foreground">
                      <tr>
                        <th className="text-left p-3">Event type</th>
                        <th className="text-right p-3">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(summary.byEventType)
                        .sort(([, a], [, b]) => Number(b) - Number(a))
                        .map(([k, v]) => (
                          <tr key={k} className="border-t">
                            <td className="p-3 font-mono text-xs">{k}</td>
                            <td className="p-3 text-right tabular-nums">{fmtMoney(Number(v))}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="coverage" className="mt-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Coverage gaps — capabilities not yet emitting billable events
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs text-muted-foreground">
                    <tr>
                      <th className="text-left p-3">Capability</th>
                      <th className="text-left p-3">Event type</th>
                      <th className="text-left p-3">Source table</th>
                      <th className="text-right p-3">Source rows (30d)</th>
                      <th className="text-right p-3">Emitted</th>
                      <th className="text-right p-3">Gap %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(gapsData?.gaps ?? []).map((g: any) => (
                      <tr key={g.event_type} className="border-t">
                        <td className="p-3">{g.capability}</td>
                        <td className="p-3 font-mono text-xs">{g.event_type}</td>
                        <td className="p-3 text-xs text-muted-foreground">{g.source_table}</td>
                        <td className="p-3 text-right tabular-nums">
                          {Number(g.source_rows).toLocaleString()}
                        </td>
                        <td className="p-3 text-right tabular-nums">
                          {Number(g.emitted_events).toLocaleString()}
                        </td>
                        <td className="p-3 text-right">
                          <Badge
                            variant={Number(g.gap_pct) >= 50 ? "destructive" : "outline"}
                            className="text-[10px]"
                          >
                            {Number(g.gap_pct)}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!explainId} onOpenChange={(o) => !o && setExplainId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Charge explanation</DialogTitle>
          </DialogHeader>
          {!explanation ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : (
            <ExplanationView data={explanation.explanation} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!disputeRow} onOpenChange={(o) => !o && setDisputeRow(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Flag charge as disputed</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {disputeRow && (
              <div className="rounded border p-3 text-sm bg-muted/30">
                <div className="font-mono text-xs">{disputeRow.event_type}</div>
                <div className="tabular-nums font-medium">
                  {fmtMoney(Number(disputeRow.amount_cents))}
                </div>
              </div>
            )}
            <div>
              <Label>Reason</Label>
              <Textarea
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                rows={4}
                placeholder="Why is this charge being disputed?"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDisputeRow(null)}>
                Cancel
              </Button>
              <Button onClick={onSubmitDispute} disabled={disputeReason.trim().length < 3}>
                Flag dispute
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SummaryCard({
  label,
  value,
  sub,
  icon: Icon,
}: {
  label: string;
  value: string;
  sub: string;
  icon: any;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon className="h-3.5 w-3.5" /> {label}
        </div>
        <div className="mt-2 text-2xl font-semibold tabular-nums">{value}</div>
        <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>
      </CardContent>
    </Card>
  );
}

function ExplanationView({ data }: { data: any }) {
  if (!data) return null;
  const ledger = data.ledger ?? {};
  const event = data.event ?? {};
  const alts = (data.alternate_policies ?? []) as any[];
  const dispute = data.dispute;
  const snap = ledger.policy_snapshot ?? {};
  return (
    <div className="space-y-4 text-sm">
      <Section title="What happened">
        <Row k="Event type" v={ledger.event_type ?? "—"} mono />
        <Row k="When" v={new Date(ledger.occurred_at).toLocaleString()} />
        <Row k="Quantity" v={String(ledger.quantity)} />
        <Row k="Source" v={event?.source_type ? `${event.source_type} · ${event.source_id}` : "manual / no source"} mono />
        <Row k="Actor" v={event?.actor_id ?? "system"} mono />
      </Section>
      <Section title="Why this amount">
        <Row k="Policy" v={snap.policy_name ?? "—"} />
        <Row k="Unit rate" v={fmtMoney(Number(ledger.unit_rate_cents))} />
        <Row k="Free allowance" v={String(snap.free_allowance ?? 0)} />
        <Row k="Amount" v={fmtMoney(Number(ledger.amount_cents))} />
        <Row k="Priority" v={String(snap.priority ?? "—")} />
      </Section>
      {alts.length > 0 && (
        <Section title="What other policies would have matched">
          <div className="space-y-1">
            {alts.map((a) => (
              <div key={a.policy_id} className="flex justify-between text-xs">
                <span>
                  {a.name}{" "}
                  {a.customer_specific && (
                    <Badge variant="outline" className="ml-1 text-[10px]">
                      customer
                    </Badge>
                  )}
                </span>
                <span className="tabular-nums">{fmtMoney(Number(a.unit_rate_cents ?? 0))}</span>
              </div>
            ))}
          </div>
        </Section>
      )}
      {dispute && (
        <Section title="Dispute">
          <Row k="Status" v={dispute.status} />
          <Row k="Reason" v={dispute.reason} />
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-muted-foreground">{k}</span>
      <span className={mono ? "font-mono" : ""}>{v}</span>
    </div>
  );
}